<template>
  <v-menu v-model="menu" :close-on-content-click="false" location="bottom" min-width="300" :scrim="false"
    @update:model-value="handleMenuToggle">
    <template #activator="{ props }">
      <v-text-field v-bind="props" :model-value="displayText" :label="label" :placeholder="placeholder"
        :clearable="clearable" :variant="variant" :density="density" :hide-details="hideDetails" readonly
        prepend-inner-icon="mdi-calendar" @click:clear="handleClear" rounded="pill"></v-text-field>
    </template>

    <v-card>
      <!-- 年份范围导航 -->
      <v-toolbar density="compact" flat>
        <v-btn icon="mdi-chevron-left" @click="prevDecade"></v-btn>
        <v-toolbar-title>{{ decadeRange }}</v-toolbar-title>
        <v-btn icon="mdi-chevron-right" @click="nextDecade"></v-btn>
      </v-toolbar>

      <!-- 年份网格 -->
      <v-container fluid class="pa-2">
        <v-row dense>
          <v-col v-for="year in displayedYears" :key="year" cols="4">
            <v-card :variant="isYearSelected(year) ? 'elevated' : 'outlined'" :color="getYearColor(year)" :class="[
              'text-center cursor-pointer',
              { 'text-disabled': !isYearInRange(year) }
            ]" @click="toggleYear(year)" flat>
              <v-card-text>{{ year }}</v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- 操作按钮 -->
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="cancelSelection">
          取消
        </v-btn>
        <v-btn variant="text" color="#2655f1" @click="confirmSelection">
          确认
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

// Props 定义
const props = defineProps({
  modelValue: {
    type: Array, // 预期格式: [2024, 2025, ...]
    default: () => []
  },
  label: {
    type: String,
    default: '选择年份'
  },
  placeholder: {
    type: String,
    default: '请选择年份'
  },
  multiple: {
    type: Boolean,
    default: false // 是否支持多选年份
  },
  range: {
    type: Boolean,
    default: false // 是否支持范围选择（起始年-结束年）
  },
  clearable: {
    type: Boolean,
    default: true
  },
  variant: {
    type: String,
    default: 'outlined'
  },
  density: {
    type: String,
    default: 'compact'
  },
  hideDetails: {
    type: Boolean,
    default: false
  },
  minYear: {
    type: Number,
    default: 1900
  },
  maxYear: {
    type: Number,
    default: 2100
  },
  // 首次打开时显示的起始年份（默认当前年所在十年）
  initialYear: {
    type: Number,
    default: () => new Date().getFullYear()
  },
  // 每页显示的年份数量
  yearsPerPage: {
    type: Number,
    default: 12
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'change'])

// 状态
const menu = ref(false)
const startYear = ref(Math.floor(props.initialYear / 10) * 10)

// 临时选中的年份
const tempSelectedYears = ref(new Set())

// 临时选中的范围（用于范围选择模式）
const tempRangeStart = ref(null)
const tempRangeEnd = ref(null)

// 备份原始选中值
const originalSelectedYears = ref(new Set())
const originalRangeStart = ref(null)
const originalRangeEnd = ref(null)

// 计算显示的年份
const displayedYears = computed(() => {
  const years = []
  for (let i = 0; i < props.yearsPerPage; i++) {
    years.push(startYear.value + i)
  }
  return years
})

// 计算当前十年范围显示文本
const decadeRange = computed(() => {
  const endYear = startYear.value + props.yearsPerPage - 1
  return `${startYear.value} - ${endYear}`
})

// 显示文本
const displayText = computed(() => {
  if (props.range) {
    if (tempRangeStart.value && tempRangeEnd.value) {
      const start = Math.min(tempRangeStart.value, tempRangeEnd.value)
      const end = Math.max(tempRangeStart.value, tempRangeEnd.value)
      return `${start} - ${end}`
    } else if (tempRangeStart.value) {
      return `${tempRangeStart.value} -`
    }
    return ''
  } else {
    const years = Array.from(tempSelectedYears.value).sort((a, b) => a - b)
    if (years.length === 0) return ''
    if (years.length === 1) return years[0].toString()
    return `已选 ${years.length} 年`
  }
})

// 初始化选中值
watch(() => props.modelValue, (newVal) => {
  if (props.range) {
    if (Array.isArray(newVal) && newVal.length >= 2) {
      tempRangeStart.value = newVal[0]
      tempRangeEnd.value = newVal[1]
    } else {
      tempRangeStart.value = null
      tempRangeEnd.value = null
    }
    // 备份原始值
    originalRangeStart.value = tempRangeStart.value
    originalRangeEnd.value = tempRangeEnd.value
  } else {
    const set = new Set(Array.isArray(newVal) ? newVal : [])
    tempSelectedYears.value = set
    originalSelectedYears.value = new Set(set)
  }
}, { immediate: true })

// 处理菜单打开/关闭
function handleMenuToggle(isOpen) {
  if (isOpen) {
    backupOriginalSelection()
  } else {
    resetToOriginal()
  }
}

// 备份原始选中值
function backupOriginalSelection() {
  if (props.range) {
    originalRangeStart.value = tempRangeStart.value
    originalRangeEnd.value = tempRangeEnd.value
  } else {
    originalSelectedYears.value = new Set(tempSelectedYears.value)
  }
}

// 恢复到原始选中值
function resetToOriginal() {
  if (props.range) {
    tempRangeStart.value = originalRangeStart.value
    tempRangeEnd.value = originalRangeEnd.value
  } else {
    tempSelectedYears.value = new Set(originalSelectedYears.value)
  }
}

// 判断年份是否在可选范围内
function isYearInRange(year) {
  return year >= props.minYear && year <= props.maxYear
}

// 判断年份是否被选中
function isYearSelected(year) {
  if (props.range) {
    if (tempRangeStart.value && tempRangeEnd.value) {
      const start = Math.min(tempRangeStart.value, tempRangeEnd.value)
      const end = Math.max(tempRangeStart.value, tempRangeEnd.value)
      return year >= start && year <= end
    }
    return year === tempRangeStart.value
  } else {
    return tempSelectedYears.value.has(year)
  }
}

// 获取年份卡片的颜色
function getYearColor(year) {
  if (!isYearInRange(year)) return 'grey-lighten-2'

  if (props.range) {
    if (year === tempRangeStart.value) return '#2655f1'
    if (year === tempRangeEnd.value) return '#2655f1'
    if (tempRangeStart.value && tempRangeEnd.value) {
      const start = Math.min(tempRangeStart.value, tempRangeEnd.value)
      const end = Math.max(tempRangeStart.value, tempRangeEnd.value)
      if (year > start && year < end) return 'primary-lighten-4'
    }
  }

  return tempSelectedYears.value.has(year) ? '#2655f1' : ''
}

// 切换年份选择
function toggleYear(year) {
  if (!isYearInRange(year)) return

  if (props.range) {
    // 范围选择模式
    if (!tempRangeStart.value) {
      // 第一次点击，设置起始年份
      tempRangeStart.value = year
      tempRangeEnd.value = null
    } else if (!tempRangeEnd.value) {
      // 第二次点击，设置结束年份
      if (year === tempRangeStart.value) {
        // 点击同一个年份，清除选择
        tempRangeStart.value = null
      } else {
        tempRangeEnd.value = year
      }
    } else {
      // 已有完整范围，重新开始
      tempRangeStart.value = year
      tempRangeEnd.value = null
    }
  } else if (props.multiple) {
    // 多选模式
    const set = new Set(tempSelectedYears.value)
    if (set.has(year)) {
      set.delete(year)
    } else {
      set.add(year)
    }
    tempSelectedYears.value = set
  } else {
    // 单选模式
    const set = new Set()
    set.add(year)
    tempSelectedYears.value = set
  }
}

// 确认选择
function confirmSelection() {
  let result = []

  if (props.range) {
    if (tempRangeStart.value && tempRangeEnd.value) {
      const start = Math.min(tempRangeStart.value, tempRangeEnd.value)
      const end = Math.max(tempRangeStart.value, tempRangeEnd.value)
      result = [start, end]
    } else if (tempRangeStart.value) {
      result = [tempRangeStart.value]
    }

    // 备份原始值
    originalRangeStart.value = tempRangeStart.value
    originalRangeEnd.value = tempRangeEnd.value
  } else {
    result = Array.from(tempSelectedYears.value).sort((a, b) => a - b)
    originalSelectedYears.value = new Set(tempSelectedYears.value)
  }

  emit('update:modelValue', result)
  emit('change', result)
  menu.value = false
}

// 取消选择
function cancelSelection() {
  resetToOriginal()
  menu.value = false
}

// 清除
function handleClear() {
  if (props.range) {
    tempRangeStart.value = null
    tempRangeEnd.value = null
  } else {
    tempSelectedYears.value = new Set()
  }
  confirmSelection()
}

// 上一页（十年）
function prevDecade() {
  if (startYear.value - props.yearsPerPage >= props.minYear) {
    startYear.value -= props.yearsPerPage
  }
}

// 下一页（十年）
function nextDecade() {
  if (startYear.value + props.yearsPerPage <= props.maxYear) {
    startYear.value += props.yearsPerPage
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.text-disabled {
  opacity: 0.5;
  pointer-events: none;
}

:deep(.v-input__details) {
  display: none;
}
</style>
